/**
 * 联机适配层。
 * 与 data/db.js 的 StorageAdapter 同思路：玩法层只认 NetAdapter 接口，
 * 换传输实现（同机多标签 / 互联网 MQTT）不用改任何系统代码。
 *
 * 消息统一形状：{ t: 类型, from: 发送者 id, ts: 时间戳, ...负载 }
 *   hello    加入房间（带名字、颜色、位置）
 *   helloAck 回应加入者，让新人也能看见老人
 *   state    位置/朝向，10Hz
 *   bye      离开
 *   tv       电视频道（retain，新人进来直接对上）
 *   say      头顶一句话
 */

export const ROOM_TOPIC = (room) => `dongtianju/v1/${room}`;

export class NetAdapter {
  constructor(room = 'public') {
    this.room = String(room || 'public').slice(0, 24);
    this.selfId = 'p' + Math.random().toString(36).slice(2, 8) + Date.now().toString(36).slice(-4);
    this.handlers = new Set();
    this.open = false;
    this.kind = 'none';
    this.offset = 0; // 服务器时间 - 本机时间，用于跨设备对齐电视进度
    // 身份由 NetProxy 注入（同一个对象引用，重连不换人）；缺省退回传输层的随机 id
    this.identity = { uid: this.selfId, name: '', tab: '', verified: false };
  }

  /** 统一封包：每条消息都带 uid（账号身份）与 tab（本窗口会话） */
  _payload(type, data = {}) {
    const id = this.identity || {};
    return {
      t: type,
      from: this.selfId,
      uid: id.uid || this.selfId,
      tab: id.tab || '',
      ts: this.now(),
      ...data,
    };
  }

  on(fn) { this.handlers.add(fn); return () => this.handlers.delete(fn); }

  _emit(msg) {
    for (const fn of [...this.handlers]) {
      try { fn(msg); } catch (err) { console.warn('[net] 处理消息出错', err); }
    }
  }

  /** 房间内统一的「现在」，跨设备靠 offset 校正 */
  now() { return Date.now() + this.offset; }

  async connect() { this.open = true; }

  /** @param {boolean} retain 保留消息，新人进房能立刻拿到（仅 MQTT 支持） */
  send(type, data = {}, retain = false) { void type; void data; void retain; }

  disconnect() { this.open = false; }

  clear() { this.handlers.clear(); }
}

/* ------------------------------------------------------------------ */
/* 同机多标签：BroadcastChannel，零依赖、零配置，开两个窗口就能串门       */
/* ------------------------------------------------------------------ */
export class LocalNet extends NetAdapter {
  constructor(room) {
    super(room);
    this.kind = 'local';
    this.ch = null;
  }

  async connect() {
    if (typeof BroadcastChannel === 'undefined') throw new Error('当前环境不支持多标签联机');
    this.ch = new BroadcastChannel('dongtianju:' + this.room);
    this.ch.onmessage = (e) => this._emit(e.data);
    this.open = true;
  }

  send(type, data = {}) {
    if (!this.open || !this.ch) return;
    this.ch.postMessage(this._payload(type, data));
  }

  disconnect() {
    super.disconnect();
    if (this.ch) { this.ch.close(); this.ch = null; }
  }
}

/* ------------------------------------------------------------------ */
/* 跨设备：公共 MQTT over WebSocket，无需自建后端                        */
/* ------------------------------------------------------------------ */
const MQTT_CDN = 'https://cdn.jsdelivr.net/npm/mqtt@5.10.1/dist/mqtt.min.js';

async function loadMqtt() {
  if (typeof window === 'undefined') throw new Error('MQTT 需要浏览器环境');
  if (window.mqtt) return window.mqtt;
  await new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = MQTT_CDN;
    s.onload = resolve;
    s.onerror = () => reject(new Error('联机组件加载失败，请检查网络'));
    document.head.appendChild(s);
  });
  return window.mqtt;
}

export class MqttNet extends NetAdapter {
  constructor(room, opts = {}) {
    super(room);
    this.kind = 'remote';
    this.url = opts.url || 'wss://broker.emqx.io:8084/mqtt';
    this.client = null;
  }

  async connect() {
    const mqtt = await loadMqtt();
    const client = await mqtt.connectAsync(this.url, {
      clientId: 'dtj_' + this.selfId,
      clean: true,
      keepalive: 30,
      reconnectPeriod: 2000,
      connectTimeout: 8000,
    });
    this.client = client;
    await client.subscribeAsync(ROOM_TOPIC(this.room), { qos: 0 });
    client.on('message', (topic, payload) => {
      if (topic !== ROOM_TOPIC(this.room)) return;
      try { this._emit(JSON.parse(payload.toString())); } catch { /* 忽略脏数据 */ }
    });
    this.open = true;
    this._ping();
  }

  /** 用一对往返消息估算时钟差，供电视进度对齐 */
  _ping() {
    if (!this.open) return;
    const t0 = Date.now();
    const onPong = (msg) => {
      if (msg.t !== 'pong' || msg.echo !== t0) return;
      const rtt = Date.now() - t0;
      this.offset = msg.server - t0 - rtt / 2;
      this.off && this.off();
    };
    this.off = this.on(onPong);
    this.send('ping', { echo: t0 });
  }

  /** 其它人是靠 ping/pong 校准时间的，这里回一拍 */
  answerPing(msg) {
    if (msg.t === 'ping') this.send('pong', { echo: msg.echo, server: Date.now() });
  }

  send(type, data = {}, retain = false) {
    if (!this.open || !this.client) return;
    this.client.publish(ROOM_TOPIC(this.room), JSON.stringify(this._payload(type, data)), { qos: 0, retain });
  }

  disconnect() {
    super.disconnect();
    if (this.client) { try { this.client.end(true); } catch { /* 已断开 */ } this.client = null; }
  }
}

/* ------------------------------------------------------------------ */
/* 内存网：给 Node 冒烟测试与降级使用                                    */
/* ------------------------------------------------------------------ */
export class MemoryNet extends NetAdapter {
  constructor(room) { super(room); this.kind = 'memory'; }

  async connect() { this.open = true; }

  send(type, data = {}) {
    if (!this.open) return;
    const msg = this._payload(type, data);
    setTimeout(() => this._emit(msg), 0);
  }
}

/**
 * 网管：对外表现为一个 NetAdapter，内部可以随时换实现（换房间/换模式）。
 * coop 与电视只认它一个引用，不用重建。
 */
export class NetProxy {
  constructor() {
    this.impl = null;
    this.handlers = new Set();
    // 身份对象由外部（Account）持有并就地更新，这里只存引用
    this._identity = { uid: '', name: '', token: '', verified: false, tab: '' };
  }

  /** 换身份对象时同步给当前传输实现，免得收发两端的 uid 不一致 */
  get identity() { return this._identity; }
  set identity(v) { this._identity = v; if (this.impl) this.impl.identity = v; }

  get open() { return !!this.impl && this.impl.open; }
  get selfId() { return this.impl ? this.impl.selfId : ''; }
  get kind() { return this.impl ? this.impl.kind : 'none'; }
  get room() { return this.impl ? this.impl.room : ''; }
  /** 账号身份（服务器发的 uid）；没登录时是本机 uid */
  get uid() {
    const id = (this.impl && this.impl.identity) || this._identity;
    return (id && id.uid) || this.selfId;
  }
  get tab() {
    const id = (this.impl && this.impl.identity) || this._identity;
    return (id && id.tab) || '';
  }
  get verified() {
    const id = (this.impl && this.impl.identity) || this._identity;
    return !!(id && id.verified);
  }

  now() { return this.impl ? this.impl.now() : Date.now(); }

  on(fn) { this.handlers.add(fn); return () => this.handlers.delete(fn); }

  _emit(msg) { for (const fn of [...this.handlers]) { try { fn(msg); } catch (e) { console.warn(e); } } }

  /** 换网：旧的断开，新的接上并把消息转发给既有的订阅者 */
  swap(next) {
    if (this.impl) {
      try { this.impl.disconnect(); } catch { /* 忽略 */ }
      this.impl.clear();
    }
    this.impl = next;
    if (next) {
      next.identity = this.identity;   // 换网不换人：uid / tab 跟着走
      next.on((msg) => this._emit(msg));
    }
    return next;
  }

  async connect() { if (this.impl) await this.impl.connect(); }

  send(type, data = {}, retain = false) {
    if (this.impl) this.impl.send(type, data, retain);
  }

  /** 只有 MQTT 实现需要回应 ping，这里做安全转发 */
  answerPing(msg) {
    if (this.impl && typeof this.impl.answerPing === 'function') this.impl.answerPing(msg);
  }

  disconnect() {
    if (this.impl) { try { this.impl.disconnect(); } catch { /* 忽略 */ } this.impl.clear(); }
    this.impl = null;
  }
}

/**
 * 建网。kind: 'local' 同机多标签 / 'remote' 跨设备 / 'memory' 测试
 */
export function createNet(kind = 'local', room = 'public') {
  if (kind === 'remote') return new MqttNet(room);
  if (kind === 'memory') return new MemoryNet(room);
  return new LocalNet(room);
}
